<script setup lang="ts">
  import { computed, getCurrentInstance, provide, ref } from 'vue';
  import { ClubService } from '@/api/services/club.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import V2WizardSteps from '@/components/modals/printing/wizard/V2WizardSteps.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { getClubCurrencyDefaults } from '@/domain/club/club-currency-defaults';
  import { getClubDefaultName } from '@/domain/club/club-default-name';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    IClubProgram,
    IUpsertClubProgramDto,
  } from '@/interfaces/IClubProgram';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    createClubProgramDraft,
    IClubProgramDraft,
  } from './club-program-draft';
  import V2ClubProgramStepActivate from './V2ClubProgramStepActivate.vue';
  import V2ClubProgramStepPoints from './V2ClubProgramStepPoints.vue';
  import V2ClubProgramStepBalance from './V2ClubProgramStepBalance.vue';

  const MODAL_NAME = 'v2-club-program';

  const props = withDefaults(
    defineProps<{
      /** Existing program to edit; null activates a new one. */
      programToEdit?: IClubProgram | null;
    }>(),
    { programToEdit: null }
  );

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isEdit = computed(() => !!props.programToEdit);

  const currencyDefaults = getClubCurrencyDefaults(
    restaurantStore.currency?.value ?? null
  );

  // Suggested name in the MENU's language (customer-facing), not the UI locale.
  const draft = ref<IClubProgramDraft>(
    createClubProgramDraft(
      props.programToEdit,
      getClubDefaultName(
        restaurantStore.restaurant?.languageId,
        restaurantStore.restaurant?.name ?? ''
      ),
      currencyDefaults
    )
  );
  provide('clubProgramDraft', draft);

  //#region Steps

  const stepIndex = ref(0);

  const stepLabels = computed(() => [
    i18nT('club.wizard.stepActivate'),
    i18nT('club.wizard.stepPoints'),
    i18nT('club.wizard.stepBalance'),
  ]);

  const isLastStep = computed(() => stepIndex.value === 2);

  /** Per-step validation; returns a user-facing error or null. */
  function stepError(): string | null {
    const data = draft.value;

    if (stepIndex.value === 0) {
      if (!data.name.trim()) return i18nT('club.wizard.errorNameRequired');
      if (data.branchIds !== null && data.branchIds.length === 0)
        return i18nT('club.wizard.errorNoBranches');
      return null;
    }

    if (stepIndex.value === 1) {
      if (!data.pointsEnabled) return null;
      if (
        !(data.earnUnitAmount! > 0) ||
        !(data.earnPoints! > 0) ||
        !(data.redeemPointsCost! > 0) ||
        !(data.redeemValue! > 0)
      )
        return i18nT('club.wizard.errorPointsConfig');
      if (
        data.maxRedeemPercent !== null &&
        (data.maxRedeemPercent < 1 || data.maxRedeemPercent > 100)
      )
        return i18nT('club.wizard.errorMaxRedeemPercent');
      if (data.pointsExpiryMonths !== null && !(data.pointsExpiryMonths >= 1))
        return i18nT('club.wizard.errorExpiryMonths');
      return null;
    }

    if (!data.pointsEnabled && !data.balanceEnabled)
      return i18nT('club.wizard.errorNothingEnabled');

    if (data.balanceEnabled) {
      for (const plan of data.topUpPlans) {
        if (!(plan.amount! > 0) || (plan.bonus ?? 0) < 0)
          return i18nT('club.wizard.errorPlanInvalid');
      }
    }

    return null;
  }

  function next() {
    const error = stepError();
    if (error) {
      ui.showToast(AlertType.Error, error);
      return;
    }
    stepIndex.value += 1;
  }

  function back() {
    if (stepIndex.value > 0) stepIndex.value -= 1;
  }

  //#endregion

  //#region Save

  const saving = ref(false);

  function buildDto(): IUpsertClubProgramDto {
    const data = draft.value;

    return {
      name: data.name.trim(),
      status: data.status,
      pointsEnabled: data.pointsEnabled,
      earnUnitAmount: data.pointsEnabled ? data.earnUnitAmount ?? 0 : 0,
      earnPoints: data.pointsEnabled ? data.earnPoints ?? 0 : 0,
      redeemPointsCost: data.pointsEnabled ? data.redeemPointsCost ?? 0 : 0,
      redeemValue: data.pointsEnabled ? data.redeemValue ?? 0 : 0,
      minRedeemPoints: data.minRedeemPoints ?? 0,
      maxRedeemPercent: data.maxRedeemPercent ?? 100,
      pointsExpiryMonths: data.pointsExpiryMonths,
      welcomePoints: data.welcomePoints ?? 0,
      balanceEnabled: data.balanceEnabled,
      topUpPlans: data.balanceEnabled
        ? data.topUpPlans.map((plan) => ({
            amount: plan.amount ?? 0,
            bonus: plan.bonus ?? 0,
          }))
        : null,
      minTopUp: data.minTopUp ?? 0,
      maxMemberBalance: data.maxMemberBalance,
      branchIds: data.branchIds,
      termsText: data.termsText.trim() || null,
    };
  }

  async function save() {
    if (saving.value) return;

    const error = stepError();
    if (error) {
      ui.showToast(AlertType.Error, error);
      return;
    }

    try {
      saving.value = true;
      const program = await ClubService.saveProgram(
        restaurantStore.restaurant!.id,
        buildDto()
      );
      ui.showToast(
        AlertType.Success,
        i18nT(isEdit.value ? 'club.wizard.savedToast' : 'club.wizard.createdToast')
      );
      proxy.$parent?.$emit('saved', program);
      // The modal unmounts here; don't touch reactive state afterwards.
      modals.hide(MODAL_NAME);
    } catch (e) {
      ui.serverError(e, i18nT('club.wizard.saveError'));
      saving.value = false;
    }
  }

  //#endregion

  function cancel() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="v2-modal-club-program">
    <div class="modal-header">
      <span class="modal-title">
        {{ isEdit ? $t('club.wizard.titleEdit') : $t('club.wizard.titleNew') }}
      </span>
      <V2WizardSteps :steps="stepLabels" :current="stepIndex" />
    </div>

    <div class="modal-content">
      <V2ClubProgramStepActivate v-if="stepIndex === 0" />
      <V2ClubProgramStepPoints v-else-if="stepIndex === 1" />
      <V2ClubProgramStepBalance v-else />
    </div>

    <div class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </PosUiButton>

      <div class="modal-actions-right">
        <PosUiButton
          v-if="stepIndex > 0"
          color="neutral"
          variant="outline"
          :disabled="saving"
          @click="back"
        >
          {{ $t('club.wizard.back') }}
        </PosUiButton>
        <PosUiButton v-if="!isLastStep" color="primary" @click="next">
          {{ $t('club.wizard.next') }}
        </PosUiButton>
        <PosUiButton v-else color="primary" :loading="saving" @click="save">
          {{ isEdit ? $t('save') : $t('club.wizard.activateCta') }}
        </PosUiButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal (max-height flex parent): header/actions
  // stay pinned while the body scrolls.
  .v2-modal-club-program {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-white;

    .modal-header {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;

      .modal-title {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }

    .modal-content {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4;
    }

    .modal-actions {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3;

      .modal-actions-right {
        @apply tw-flex tw-items-center tw-gap-2;
      }
    }
  }
</style>
